import { useQuery } from '@tanstack/react-query';
import type {
  VendorApplicationListItem,
  VendorApplicationStatusEnum,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import type { VendorSort } from '@/stores/useVendorFiltersStore';

export interface VendorApplicationsQueryParams {
  page?: number;
  size?: number;
  sort?: VendorSort;
  status?: ReadonlyArray<VendorApplicationStatusEnum>;
  keyword?: string;
  slaDueBefore?: string | null;
  assignedReviewerId?: string | null;
}

export interface VendorApplicationsPage {
  items: ReadonlyArray<VendorApplicationListItem>;
  meta: ApiMeta;
}

interface AdminQuery {
  page?: number;
  size?: number;
  sort?: VendorSort;
  status?: VendorApplicationStatusEnum[];
  keyword?: string;
  slaDueBefore?: string;
  assignedReviewerId?: string;
}

const cleanQuery = (params: VendorApplicationsQueryParams): AdminQuery => {
  const out: AdminQuery = {};
  if (params.page) out.page = Math.max(0, params.page - 1);
  if (params.size) out.size = params.size;
  // sort disabled (F-08): FE enum vs BE entity column mismatch; BE @PageableDefault 사용
  if (params.status && params.status.length > 0) out.status = [...params.status];
  if (params.keyword && params.keyword.trim().length > 0) out.keyword = params.keyword.trim();
  if (params.slaDueBefore) out.slaDueBefore = params.slaDueBefore;
  if (params.assignedReviewerId) out.assignedReviewerId = params.assignedReviewerId;
  return out;
};

export const vendorApplicationsQueryKey = (params: VendorApplicationsQueryParams) =>
  ['admin', 'vendor-applications', params] as const;

export const useVendorApplicationsQuery = (params: VendorApplicationsQueryParams) =>
  useQuery<VendorApplicationsPage>({
    queryKey: vendorApplicationsQueryKey(params),
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/admin/v1/vendor-applications', {
        params: { query: cleanQuery(params) },
      });
      const payload = data as { items: VendorApplicationListItem[] };
      return { items: payload.items, meta };
    },
  });
